import React, { useEffect, useRef, useState } from 'react';
import type { ContentCard as IContentCard } from './types';

interface ContentCardProps {
  card: IContentCard;
  viewMode?: 'v2' | 'v3' | 'v4' | 'vlist';
  onCardClick?: (card: IContentCard) => void;
  onSave?: (cardId: string) => void;
}

/**
 * ContentCard Component
 * Displays a single piece of content (video, audio, or guide)
 * Supports multiple view modes
 */
export const ContentCard: React.FC<ContentCardProps> = ({
  card,
  viewMode = 'v3',
  onCardClick,
  onSave,
}) => {
  const [isSaved, setIsSaved] = useState(card.isSaved || false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [shouldAutoPlayAudio, setShouldAutoPlayAudio] = useState(false);
  const [mockProgress, setMockProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const audioPath = typeof card.filePath === 'string' ? card.filePath.trim() : '';
  const isAudioCard = card.format === 'audio';
  const hasAudioSource = Boolean(audioPath);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (!hasAudioSource || !isAudioPlayerOpen || !shouldAutoPlayAudio) {
      return;
    }

    const autoPlay = async () => {
      if (!audioRef.current) {
        return;
      }

      setIsAudioLoading(true);
      try {
        await audioRef.current.play();
        setIsAudioPlaying(true);
      } catch (error) {
        setIsAudioPlaying(false);
      } finally {
        setIsAudioLoading(false);
        setShouldAutoPlayAudio(false);
      }
    };

    autoPlay();
  }, [hasAudioSource, isAudioPlayerOpen, shouldAutoPlayAudio]);

  useEffect(() => {
    if (!isAudioCard || hasAudioSource || !isAudioPlaying) {
      return;
    }

    const interval = window.setInterval(() => {
      setMockProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }

        return Math.min(prev + 2, 100);
      });
    }, 240);

    return () => window.clearInterval(interval);
  }, [isAudioCard, hasAudioSource, isAudioPlaying]);

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSaved(!isSaved);
    onSave?.(card.id);
  };

  const handleClick = () => {
    if (isAudioCard) {
      return;
    }

    onCardClick?.(card);
  };

  const handleAudioToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAudioCard) {
      onCardClick?.(card);
      return;
    }

    try {
      if (!hasAudioSource) {
        setIsAudioPlayerOpen(true);
        setIsAudioPlaying((prev) => !prev);
        return;
      }

      if (!isAudioPlayerOpen) {
        setIsAudioPlayerOpen(true);
        setShouldAutoPlayAudio(true);
        return;
      }

      if (isAudioPlaying) {
        audioRef.current?.pause();
        setIsAudioPlaying(false);
      } else {
        setIsAudioLoading(true);
        await audioRef.current?.play();
        setIsAudioPlaying(true);
      }
    } catch (error) {
      setIsAudioPlaying(false);
    } finally {
      setIsAudioLoading(false);
    }
  };

  const handleSeek = (e: React.MouseEvent, deltaSeconds: number) => {
    e.stopPropagation();

    if (!isAudioCard) {
      return;
    }

    if (!hasAudioSource) {
      setMockProgress((prev) => Math.max(0, Math.min(100, prev + (deltaSeconds > 0 ? 10 : -10))));
      return;
    }

    if (!audioRef.current) {
      return;
    }

    const current = audioRef.current.currentTime || 0;
    const duration = Number.isFinite(audioRef.current.duration) ? audioRef.current.duration : 0;
    const next = Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, current + deltaSeconds));
    audioRef.current.currentTime = next;

    if (duration > 0) {
      setMockProgress(Math.min((next / duration) * 100, 100));
    }
  };

  const formatBadgeColor =
    card.format === 'video' ? 'bg-so-600' : card.format === 'audio' ? 'bg-sb-600' : 'bg-sc-c1';

  // Premium Overlay
  if (card.isPremium) {
    return (
      <div
        onClick={handleClick}
        className="rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer border border-sb-100 relative group"
      >
        {/* Thumbnail */}
        {card.thumbnail && (
          <div className="relative aspect-video overflow-hidden">
            <img
              src={card.thumbnail}
              alt={card.title}
              className="w-full h-full object-cover group-hover:scale-104 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        {/* Premium Overlay */}
        <div className="absolute inset-0 rounded-2xl backdrop-blur-2xl bg-white/40 flex flex-col items-center justify-center gap-2 z-10">
          <div className="w-9 h-9 rounded-full bg-so-600/15 border-2 border-so-600 flex items-center justify-center">
            <svg
              width="12"
              height="14"
              viewBox="0 0 12 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              className="text-so-600"
            >
              <rect x="2" y="5" width="8" height="7" rx="1.5" />
              <path d="M3 5V4a2 2 0 0 1 4 0v1" />
            </svg>
          </div>
          <p className="text-xs font-semibold text-gray-900 tracking-widest uppercase">
            Desbloquea esta clase
          </p>
          <button className="mt-1 px-4 py-1.5 bg-gradient-to-r from-so-600 to-so-300 text-white text-xs font-semibold rounded-full hover:shadow-lg transition-shadow">
            Suscribirme
          </button>
        </div>

        {/* Badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 ${formatBadgeColor} text-white text-xs font-semibold rounded-full`}>
          {card.format === 'video' ? 'Video' : card.format === 'audio' ? 'Audio' : 'Guia'}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer border border-sb-100 group"
    >
      {/* Thumbnail / Visual */}
      {card.format === 'video' ? (
        <div className="relative aspect-video overflow-hidden">
          <img
            src={card.thumbnail}
            alt={card.title}
            className="w-full h-full object-cover group-hover:scale-104 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Play Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/95 backdrop-blur-md border-2 border-so-600 flex items-center justify-center group-hover:scale-110 group-hover:bg-so-600 transition-all shadow-lg">
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="currentColor"
                className="text-so-600 group-hover:text-white ml-0.5"
              >
                <path d="M2 1.5L13 8L2 14.5V1.5Z" />
              </svg>
            </div>
          </div>

          {/* Duration Badge */}
          <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/65 backdrop-blur-sm rounded-2xl text-white text-xs font-light tracking-wide">
            {card.duration}
          </div>

          {/* Format Badge */}
          <div className={`absolute top-3 left-3 px-2 py-1 ${formatBadgeColor} text-white text-xs font-semibold rounded-full`}>
            Video
          </div>
        </div>
      ) : card.format === 'audio' ? (
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-sb-800 via-sb-700 to-sb-600 flex items-center justify-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(241,135,0,0.22),transparent_46%),radial-gradient(circle_at_80%_80%,rgba(170,111,39,0.24),transparent_50%)]" />

          {/* Waveform */}
          <div className="relative z-10 flex items-end gap-1 px-6 w-full h-20">
            {Array.from({ length: 38 }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 rounded-sm ${isAudioPlaying ? 'bg-so-300/90' : 'bg-so-300/50'} transition-colors`}
                style={{
                  height: `${24 + Math.abs(Math.sin(i * 0.52)) * 56}%`,
                  animationName: 'pulse',
                  animationDuration: isAudioPlaying ? '0.9s' : '2.1s',
                  animationTimingFunction: 'ease-in-out',
                  animationIterationCount: 'infinite',
                  animationDelay: `${i * 0.03}s`,
                }}
              />
            ))}
          </div>

          {/* Thumbnail Player */}
          <div className="absolute inset-x-3 bottom-3 z-20 rounded-xl border border-white/20 bg-black/35 p-2 backdrop-blur-sm">
            <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full rounded-full bg-so-300 transition-all duration-300"
                style={{ width: `${hasAudioSource && audioRef.current?.duration
                  ? Math.min((audioRef.current.currentTime / audioRef.current.duration) * 100, 100)
                  : mockProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => handleSeek(e, -30)}
                  className="rounded-full bg-white/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-white/30"
                  aria-label="Retroceder 30 segundos"
                >
                  -30s
                </button>
                <button
                  onClick={handleAudioToggle}
                  disabled={isAudioLoading}
                  className="flex items-center gap-2 rounded-full bg-so-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white hover:brightness-95"
                  aria-label={isAudioPlaying ? 'Pausar audio' : 'Iniciar audio'}
                >
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20">
                    <svg width="9" height="10" viewBox="0 0 12 14" fill="white">
                      {isAudioPlaying ? <path d="M1 1H4V13H1V1ZM8 1H11V13H8V1Z" /> : <path d="M1 1L11 7L1 13V1Z" />}
                    </svg>
                  </span>
                  {isAudioLoading ? 'Cargando...' : isAudioPlaying ? 'Pausar' : 'Start'}
                </button>
                <button
                  onClick={(e) => handleSeek(e, 30)}
                  className="rounded-full bg-white/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-white/30"
                  aria-label="Avanzar 30 segundos"
                >
                  +30s
                </button>
              </div>
              <span className="text-[11px] font-medium uppercase tracking-wide text-white/85">
                {hasAudioSource ? 'Audio real' : 'Preview'}
              </span>
            </div>
            {hasAudioSource && isAudioPlayerOpen ? (
              <audio
                ref={audioRef}
                src={audioPath}
                preload="none"
                className="hidden"
                onPlay={() => setIsAudioPlaying(true)}
                onPause={() => {
                  setIsAudioPlaying(false);
                  setIsAudioLoading(false);
                }}
                onEnded={() => {
                  setIsAudioPlaying(false);
                  setIsAudioLoading(false);
                }}
                onLoadStart={() => setIsAudioLoading(true)}
                onCanPlay={() => setIsAudioLoading(false)}
                onTimeUpdate={() => setMockProgress((audioRef.current?.currentTime && audioRef.current?.duration)
                  ? Math.min((audioRef.current.currentTime / audioRef.current.duration) * 100, 100)
                  : 0)}
              />
            ) : null}
          </div>

          {/* Badges */}
          <div className="absolute top-3 left-3 px-2 py-1 bg-sb-600 text-white text-xs font-semibold rounded-full">
            Audio
          </div>
          <div className="absolute top-3 right-3 text-white/90 text-xs font-light">{card.duration}</div>
        </div>
      ) : (
        /* PDF Guide Visual */
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-sc-c2 to-sc-c2/70 flex flex-col items-center justify-center group">
          {/* Document Icon */}
          <div className="w-14 h-20 bg-white rounded border border-sb-300/40 flex flex-col items-center justify-center gap-1 p-2 shadow-lg">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-0.5 rounded-full bg-sb-200"
                style={{
                  width: `${60 + (i % 2) * 20}%`,
                }}
              />
            ))}
          </div>
          <div className="text-xs text-gray-600 mt-2">PDF</div>

          {/* Format Badge */}
          <div className="absolute top-3 left-3 px-2 py-1 bg-sc-c1 text-white text-xs font-semibold rounded-full">
            Guia
          </div>

          {/* Pattern Background */}
          <div className="absolute inset-0 opacity-20 pointer-events-none bg-repeating-linear-gradient(45deg, transparent, transparent 18px, rgba(97, 160, 164, 0.05) 18px, rgba(97, 160, 164, 0.05) 36px)" />
        </div>
      )}

      {/* Body */}
      <div className="p-5">
        {/* Meta */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-so-600 uppercase tracking-widest">
            {card.category}
          </span>
          <div className="w-1 h-1 rounded-full bg-sb-200" />
          <span className="text-xs uppercase text-gray-500">{card.level}</span>
        </div>

        {/* Title */}
        <h3 className="text-base font-medium text-gray-900 leading-tight mb-2 line-clamp-2">
          {card.title}
        </h3>

        {/* Description */}
        {viewMode !== 'v4' && (
          <p className="text-sm font-light text-gray-600 leading-relaxed mb-4 line-clamp-2">
            {card.description}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-sb-100">
          {card.instructor ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-sb-300 to-so-200 flex items-center justify-center text-xs font-semibold text-white">
                {card.instructor.initials ? card.instructor.initials : "S"}
              </div>
              <span className="text-xs text-gray-600">{card.instructor.name}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-500">Instructora</span>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            className={`w-7 h-7 rounded-full border-1.5 flex items-center justify-center transition-all ${
              isSaved
                ? 'bg-so-600/15 border-so-600'
                : 'bg-sb-100 border-sb-200 hover:bg-so-100 hover:border-so-600'
            }`}
            aria-label="Guardar para despues"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill={isSaved ? 'currentColor' : 'none'}
              stroke={isSaved ? '#B14A36' : 'currentColor'}
              strokeWidth="1.5"
              className={isSaved ? 'text-so-600' : 'text-sb-600'}
            >
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            </svg>
          </button>
        </div>

        {isAudioCard ? <p className="mt-3 text-xs text-sb-700/80">Reproduccion interna dentro del thumbnail.</p> : null}
      </div>
    </div>
  );
};

export default ContentCard;
